import React from 'react'
import { motion } from 'framer-motion'
import { ServiceType } from '../types'
import { getServiceConfig } from '../data/services'
import { HeaderNav } from './HeaderNav'
import { Breadcrumb } from './Breadcrumb'
import { SEOHead } from './SEO/SEOHead'
import { useNavigation } from '../hooks/useNavigation'
import { useSEO } from '../hooks/useSEO'
import { HRServicePage } from './HRServicePage'
import { VerpackGServicePage } from './VerpackGServicePage'
import { ComingSoonServicePage } from './ComingSoonServicePage'

interface ServicePageProps {
  serviceType: ServiceType
}

export const ServicePage: React.FC<ServicePageProps> = ({ serviceType }) => {
  // Route to specific service pages when they exist
  switch (serviceType) {
    case ServiceType.HR:
      return <HRServicePage />
    case ServiceType.VERPACKG:
      return <VerpackGServicePage />
    case ServiceType.COMPLIANCE:
    case ServiceType.TRADE:
    case ServiceType.INTELLIGENCE:
      const service = getServiceConfig(serviceType)
      return <ComingSoonServicePage service={service} onBack={() => window.history.back()} />
    default:
      // Fallback to generic service page
      break
  }

  // Generic service page implementation
  const service = getServiceConfig(serviceType)
  const { navigationState, handleToggleMobileMenu } = useNavigation()

  // Apply SEO for this service page
  useSEO({ serviceType })

  return (
    <>
      <SEOHead serviceType={serviceType} />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        {/* Header Navigation */}
        <HeaderNav
          currentService={serviceType}
          isScrolled={navigationState.isScrolled}
          showMobileMenu={navigationState.showMobileMenu}
          onToggleMobileMenu={handleToggleMobileMenu}
        />

        {/* Main Content */}
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <div className="py-6">
              <Breadcrumb path={navigationState.breadcrumbPath} />
            </div>

            {/* Service Hero Section */}
            <motion.div
              className="text-center py-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div
                className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center text-white text-2xl font-bold"
                style={{ backgroundColor: service.accentColor }}
              >
                {service.name.charAt(0)}
              </div>

              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                {service.name}
              </h1>

              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                {service.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  className="px-8 py-4 text-white font-bold rounded-xl shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                  style={{ backgroundColor: service.accentColor }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {service.ctaText}
                </motion.button>

                <motion.button
                  className="px-8 py-4 bg-white text-gray-700 font-bold rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Learn More
                </motion.button>
              </div>
            </motion.div>

            {/* Features Section */}
            <motion.div
              className="py-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
                What's Included
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {service.features.map((feature, index) => (
                  <motion.div
                    key={index}
                    className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 * index }}
                  >
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${service.accentColor}20` }}
                    >
                      <div
                        className="w-6 h-6 rounded-full"
                        style={{ backgroundColor: service.accentColor }}
                      />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {feature}
                    </h3>
                    <p className="text-gray-600">
                      Professional implementation of {feature.toLowerCase()} tailored to your business needs.
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Pricing Section */}
            <motion.div
              className="py-16 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Simple, Transparent Pricing
              </h2>

              <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md mx-auto">
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {service.pricing}
                </div>
                <p className="text-gray-600 mb-6">
                  {service.shortDescription}
                </p>

                <motion.button
                  className="w-full px-8 py-4 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                  style={{ backgroundColor: service.accentColor }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {service.ctaText}
                </motion.button>
              </div>
            </motion.div>

            {/* Coming Soon Notice for inactive services */}
            {!service.isActive && (
              <motion.div
                className="py-16 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 max-w-2xl mx-auto">
                  <div className="text-amber-600 text-lg font-semibold mb-2">
                    Coming Soon
                  </div>
                  <p className="text-amber-700">
                    This service is currently in development. Sign up to be notified when it becomes available.
                  </p>
                  <motion.button
                    className="mt-4 px-6 py-3 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 transition-colors duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Notify Me
                  </motion.button>
                </div>
              </motion.div>
            )}
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12 mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                <img
                  src="/mernaki-32.png"
                  alt="Mernaki Logo"
                  className="w-6 h-6 object-contain"
                />
              </div>
              <span className="text-2xl font-bold">Mernaki</span>
            </div>
            <p className="text-gray-400 mb-4">
              Your trusted partner for comprehensive business solutions.
            </p>
            <p className="text-gray-500">
              &copy; {new Date().getFullYear()} Mernaki. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  )
}